import { createSlice, createSelector } from "@reduxjs/toolkit";
import { coreApi } from "api/core";
import { getGroupingTour } from "util/tours/tourMergeHotfix";
import { setShowMessage } from "redux/slices/uiSlice";
import { getApiErrorMessage, translateApiErrorMessage } from "util/handle-error";
import i18n from "i18next";

const baseUrl = "/orders";
const initialState = {
  orders: [],
  timestamp: null,
  order: null,
  loading: false,
};

const orderSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    setOrder: (state, action) => {
      state.order = action.payload;
    },
    setOrders: (state, action) => {
      state.orders = action.payload;
      state.timestamp = +new Date();
    },
    setOrderLoading: (state) => {
      state.loading = true;
    },
    setOrderReady: (state) => {
      state.loading = false;
    },
    clearOrders: (state) => {
      state.orders = [];
      state.order = null;
      state.timestamp = null;
    },
  },
});

export const { setOrder, setOrders, setOrderLoading, setOrderReady, clearOrders } = orderSlice.actions;
export default orderSlice.reducer;

export const getOrder = (id) => async (dispatch) => {
  const url = baseUrl + `/${id}`;
  dispatch(setOrderLoading());

  try {
    const res = await coreApi.fetch(url);
    dispatch(setOrder(res));
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setOrderReady());
  }
};

export const getOrders = () => async (dispatch) => {
  dispatch(setOrderLoading());

  try {
    const orders = await coreApi.fetch(`${baseUrl}`);

    const grouped = orders.reduce((acc, order, i) => {
      const Tour = getGroupingTour(order);
      if (!Tour?.id) {
        return acc;
      }
      const key = `${Tour.id}-${order.departure}`;

      if (!acc[key]) {
        acc[key] = {
          Tour,
          orders: [],
          id: i,
          mainCheck: false,
        };
      }

      acc[key].orders.push(order);

      return acc;
    }, {});

    dispatch(setOrders(Object.values(grouped)));
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setOrderReady());
  }
};

export const addOrder = (payload) => async (dispatch) => {
  dispatch(setOrderLoading());

  try {
    const order = await coreApi.post(baseUrl, payload);
    dispatch(clearOrders());
    if (Array.isArray(order?.deposit_agreement_warnings) && order.deposit_agreement_warnings.length) {
      dispatch(
        setShowMessage({
          description: i18n.t("No deposit agreement available. Please add a deposit agreement."),
          type: "warning",
        })
      );
    } else {
      dispatch(
        setShowMessage({
          description: "Order created successfully",
          type: "success",
        })
      );
    }
    return order;
  } catch (err) {
    console.error(err);

    const errorMessage = translateApiErrorMessage(
      getApiErrorMessage(err, "Failed creating order. Please try again later")
    );

    dispatch(
      setShowMessage({
        description: errorMessage,
        type: "error",
      })
    );
    throw err;
  } finally {
    dispatch(setOrderReady());
  }
};

export const editOrder = (id, payload) => async (dispatch) => {
  const url = baseUrl + `/${id}`;
  dispatch(setOrderLoading());

  try {
    const res = await coreApi.put(url, payload);

    dispatch(clearOrders());
    if (Array.isArray(res?.deposit_agreement_warnings) && res.deposit_agreement_warnings.length) {
      dispatch(
        setShowMessage({
          description: i18n.t("No deposit agreement available. Please add a deposit agreement."),
          type: "warning",
        })
      );
    } else {
      dispatch(
        setShowMessage({
          description: i18n.t("Order modified successfully"),
          type: "success",
        })
      );
    }

    return res;
  } catch (err) {
    console.error(err);
    dispatch(
      setShowMessage({
        description: translateApiErrorMessage(
          getApiErrorMessage(err, "Failed editing order. Please try again later")
        ),
        type: "error",
      })
    );
    throw err;
  } finally {
    dispatch(setOrderReady());
  }
};

export const deleteOrder = (id, softDelete = false) => async (dispatch) => {
  const url = baseUrl + `/${id}?softDelete=${softDelete}`;
  dispatch(setOrderLoading());

  try {
    const res = await coreApi.delete(url);

    dispatch(
      setShowMessage({
        description: "Order deleted successfully",
        type: "success",
      })
    );
    dispatch(clearOrders());

    return res;
  } catch (err) {
    console.error(err);
    dispatch(
      setShowMessage({
        description: err?.response?.data?.error ?? "Failed deleting order.",
        type: "error",
      })
    );
  } finally {
    dispatch(setOrderReady());
  }
};

export const fetchAvailableDepartures = (params) => async () => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  ).toString();

  return await coreApi.fetch(`${baseUrl}/available-departures?${query}`);
};

export const skipOrder = (routeId, orderId, reason) => async (dispatch) => {
  dispatch(setOrderLoading());

  try {
    const res = await coreApi.post(`/orders/${routeId}/${orderId}/skip`, { reason });

    dispatch(
      setShowMessage({
        description: "Order skipped successfully",
        type: "success",
      })
    );

    return res;
  } catch (err) {
    console.error(err);

    dispatch(
      setShowMessage({
        description: err?.response?.data?.error || "Failed to skip order",
        type: "error",
      })
    );
    throw err;
  } finally {
    dispatch(setOrderReady());
  }
};

const orderSelector = ({ orders }) => orders.order;
const ordersSelector = ({ orders }) => orders.orders;
const orderStatusSelector = ({ orders }) => orders.loading;

export const selectOrder = createSelector(orderSelector, (order) => order);
export const selectOrders = createSelector(ordersSelector, (orders) => orders);
export const selectOrderStatus = createSelector(orderStatusSelector, (loading) => loading);
export const selectOrdersTimestamp = createSelector(
  ({ orders }) => orders.timestamp,
  (o) => o
);
